import { useTranslation } from 'react-i18next';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Trash2, Download, Share2, FolderOpen, Copy } from 'lucide-react';
import { CURRENCIES } from '@/lib/calculationEngine';

const getCurrencySymbol = (est) => CURRENCIES.find(c => c.value === est?.config?.currency)?.symbol || '$';
const formatCost = (val, sym) => `${sym}${(val || 0).toLocaleString()}`;

/**
 * Single saved-estimate row. Click-to-select for comparison, plus inline
 * Open/PDF/Share/Delete actions in the top right.
 */
export function EstimateCard({
  estimate,
  isSelected,
  isDemo,
  sharing,
  onToggleSelect,
  onOpen,
  onExportPdf,
  onShare,
  onDelete,
  onDuplicate,
}) {
  const { t, i18n } = useTranslation();
  const sym = getCurrencySymbol(estimate);

  return (
    <Card
      className={`bg-white border min-w-0 transition-colors cursor-pointer ${
        isSelected
          ? 'border-[#527F19] ring-1 ring-[#78B928]/30 shadow-card'
          : 'border-[#DDE1E5] hover:border-[#527F19]/40 hover:shadow-raised'
      }`}
      onClick={() => onToggleSelect(estimate.estimate_id)}
      data-testid={`estimate-card-${estimate.estimate_id}`}
    >
      <CardContent className="p-5">
        <div className="flex items-start gap-4">
          <div className="pt-0.5">
            <Checkbox
              checked={isSelected}
              aria-label={`${t('scenarios.select')}: ${estimate.name}`}
              onCheckedChange={() => onToggleSelect(estimate.estimate_id)}
              onClick={(e) => e.stopPropagation()}
              data-testid={`select-${estimate.estimate_id}`}
            />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex flex-wrap items-start justify-between gap-x-4 gap-y-2">
              <div className="min-w-0 w-full sm:w-auto sm:flex-1">
                <h3
                  className="font-display font-semibold text-[#171A1D] break-words"
                  data-testid={`estimate-name-${estimate.estimate_id}`}
                >
                  {estimate.name}
                </h3>
                <div className="mt-1 flex flex-wrap items-center gap-2 font-data text-[10px] uppercase tracking-[0.08em]">
                  {estimate.confidence && (
                    <span className="text-[#66707A]" data-testid={`confidence-badge-${estimate.estimate_id}`}>
                      {t('trust.confidenceLabel')}: {t(`trust.${estimate.confidence}`)}
                    </span>
                  )}
                  {estimate.parent_estimate_id && (
                    <span className="text-[#2867B2]" data-testid={`scenario-badge-${estimate.estimate_id}`}>
                      {t('estimates.scenario')}
                    </span>
                  )}
                  {estimate.legacy && (
                    <span
                      className="text-[#66707A] border border-[#DDE1E5] bg-[#F5F6F7] px-1.5 py-0.5 rounded-sm"
                      title={t('trust.legacyHint')}
                      data-testid={`legacy-badge-${estimate.estimate_id}`}
                    >
                      {t('trust.legacyBadge')}
                    </span>
                  )}
                </div>
              </div>
              <div className="flex flex-wrap items-center gap-1 shrink-0">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={(e) => { e.stopPropagation(); onOpen(estimate); }}
                  className="text-[#527F19] hover:text-[#527F19] hover:bg-[#527F19]/10"
                  title={t('estimates.openInCalculator')}
                  aria-label={t('estimates.openInCalculator')}
                  data-testid={`open-${estimate.estimate_id}`}
                >
                  <FolderOpen className="w-4 h-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={(e) => { e.stopPropagation(); onExportPdf(estimate); }}
                  className="text-[#66707A] hover:text-[#171A1D] hover:bg-[#F5F6F7]"
                  title={t('estimates.exportPdfAction')}
                  aria-label={t('estimates.exportPdfAction')}
                  data-testid={`pdf-${estimate.estimate_id}`}
                >
                  <Download className="w-4 h-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={(e) => { e.stopPropagation(); onShare(estimate.estimate_id); }}
                  className="text-[#66707A] hover:text-[#171A1D] hover:bg-[#F5F6F7]"
                  disabled={sharing === estimate.estimate_id}
                  title={t('estimates.shareAction')}
                  aria-label={t('estimates.shareAction')}
                  data-testid={`share-${estimate.estimate_id}`}
                >
                  <Share2 className="w-4 h-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-[#66707A] hover:text-[#171A1D] hover:bg-[#F5F6F7]"
                  disabled={isDemo}
                  onClick={(e) => { e.stopPropagation(); onDuplicate(estimate.estimate_id); }}
                  title={t('estimates.duplicate')}
                  aria-label={t('estimates.duplicate')}
                  data-testid={`duplicate-${estimate.estimate_id}`}
                >
                  <Copy className="w-4 h-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={(e) => { e.stopPropagation(); onDelete(estimate.estimate_id); }}
                  className="text-[#B42318] hover:text-[#B42318] hover:bg-red-500/10"
                  disabled={isDemo}
                  title={t('estimates.deleteAction')}
                  aria-label={t('estimates.deleteAction')}
                  data-testid={`delete-${estimate.estimate_id}`}
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            </div>
            <div className="flex flex-wrap gap-x-6 gap-y-1 mt-2 text-sm">
              <span className="text-[#8A929B]">
                {new Date(estimate.created_at).toLocaleDateString(i18n.language, { month: 'short', day: 'numeric', year: 'numeric' })}
              </span>
              <span className="text-[#8A929B]">
                {estimate.config?.industry ? t(`calcData.industries.${estimate.config.industry}`) : '-'}
              </span>
              <span className="text-[#8A929B]">
                {estimate.config?.companySize ? t(`calcData.sizes.${estimate.config.companySize}`) : '-'}
              </span>
            </div>
            <div className="flex flex-wrap gap-x-8 gap-y-2 mt-3 pt-3 border-t border-[#F5F6F7]">
              <div>
                <div className="text-[10px] font-semibold uppercase tracking-wider text-[#8A929B]">{t('estimates.effortLabel')}</div>
                <div className="font-data text-sm font-medium text-[#171A1D]" data-testid={`effort-${estimate.estimate_id}`}>
                  {estimate.results?.totalEffort || '-'} {t('estimates.days')}
                </div>
              </div>
              <div>
                <div className="text-[10px] font-semibold uppercase tracking-wider text-[#8A929B]">{t('estimates.costLabel')}</div>
                <div className="font-data text-sm font-semibold text-[#171A1D]" data-testid={`cost-${estimate.estimate_id}`}>
                  {estimate.results?.totalCost ? formatCost(estimate.results.totalCost, sym) : '-'}
                </div>
                {estimate.results?.costRange && (
                  <div className="font-data text-[11px] text-[#8A929B]" data-testid={`range-${estimate.estimate_id}`}>
                    {formatCost(estimate.results.costRange.low, sym)} – {formatCost(estimate.results.costRange.high, sym)}
                  </div>
                )}
              </div>
              <div>
                <div className="text-[10px] font-semibold uppercase tracking-wider text-[#8A929B]">{t('estimates.timelineLabel')}</div>
                <div className="font-data text-sm font-medium text-[#171A1D]">
                  {estimate.results?.totalDurationWeeks || '-'} {t('estimates.weeks')}
                </div>
              </div>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
